import { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle, Phone, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { useLanguage } from "../contexts/LanguageContext";

const FAQPage = () => {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      id: 'cat-services',
      category: "Services",
      questions: [
        {
          id: 'q-types-services',
          q: "Quels types de services de nettoyage proposez-vous ?",
          a: "Nettis Facility propose une gamme complète de services : nettoyage de bureaux, nettoyage industriel, entretien d'immeubles et copropriétés, nettoyage après travaux, désinfection professionnelle et nettoyage de vitres. Nous intervenons pour les entreprises, industries et syndics de copropriété en Belgique."
        },
        {
          id: 'q-particuliers',
          q: "Intervenez-vous pour des particuliers ?",
          a: "Nettis Facility se spécialise principalement dans le nettoyage professionnel pour les entreprises, industries et copropriétés. Pour les particuliers, nous pouvons intervenir dans certains cas spécifiques comme le nettoyage après travaux ou le nettoyage de fin de chantier. Contactez-nous pour discuter de votre projet."
        },
        {
          id: 'q-desinfection',
          q: "Proposez-vous des services de désinfection ?",
          a: "Oui, nous proposons des services de désinfection professionnelle utilisant des produits certifiés et des techniques éprouvées. Nos protocoles respectent les normes sanitaires en vigueur et sont adaptés à chaque type d'environnement (bureaux, espaces médicaux, industries alimentaires, etc.)."
        }
      ]
    },
    {
      id: 'cat-tarifs',
      category: "Tarifs et devis",
      questions: [
        {
          id: 'q-devis',
          q: "Comment obtenir un devis pour mes locaux ?",
          a: "Vous pouvez obtenir un devis gratuit et personnalisé en remplissant notre formulaire de contact en ligne, en nous appelant au +32 2 880 66 50, ou en nous envoyant un email à sales@nettis.be. Nous vous répondons généralement sous 24-48h avec une proposition adaptée à vos besoins."
        },
        {
          id: 'q-tarifs',
          q: "Quels sont vos tarifs ?",
          a: "Nos tarifs dépendent de plusieurs facteurs : la surface à nettoyer, le type de locaux, la fréquence d'intervention et les services spécifiques demandés. Nous établissons toujours un devis personnalisé après évaluation de vos besoins. Contactez-nous pour une estimation gratuite."
        },
        {
          id: 'q-contrats',
          q: "Proposez-vous des contrats à long terme ?",
          a: "Oui, nous proposons des contrats de maintenance régulière (quotidien, hebdomadaire, mensuel) avec des tarifs préférentiels. Ces contrats garantissent un suivi régulier et une qualité constante. La durée et les conditions sont flexibles et adaptées à vos besoins."
        }
      ]
    },
    {
      id: 'cat-zones',
      category: "Zone d'intervention",
      questions: [
        {
          id: 'q-regions',
          q: "Dans quelles régions intervenez-vous ?",
          a: "Nettis Facility intervient dans toute la Belgique, avec une forte présence à Bruxelles et sa périphérie, en Wallonie (Liège, Namur, Charleroi, Mons) et en Flandre. Notre siège est situé Avenue Louise 65 à Bruxelles."
        }
      ]
    },
    {
      id: 'cat-qualite',
      category: "Qualité et engagement",
      questions: [
        {
          id: 'q-formation',
          q: "Vos employés sont-ils formés et assurés ?",
          a: "Absolument. Tous nos agents de nettoyage sont formés aux techniques professionnelles, aux normes de sécurité et à l'utilisation des équipements. Nous sommes entièrement assurés (RC professionnelle) pour toutes nos interventions."
        },
        {
          id: 'q-qualite',
          q: "Comment garantissez-vous la qualité de vos prestations ?",
          a: "Nous avons mis en place un système de contrôle qualité rigoureux : visites de supervision régulières, cahiers de liaison, retours clients systématiques et audits périodiques. Si vous n'êtes pas satisfait, nous intervenons rapidement pour corriger la situation."
        }
      ]
    },
    {
      id: 'cat-recrutement',
      category: "Recrutement",
      questions: [
        {
          id: 'q-postuler',
          q: "Comment postuler chez Nettis Facility ?",
          a: "Vous pouvez postuler directement sur notre page Carrières en remplissant le formulaire de candidature. Nous recherchons régulièrement des agents de nettoyage, chefs d'équipe et responsables de secteur. Envoyez votre CV à job@nettis.be ou utilisez notre formulaire en ligne."
        },
        {
          id: 'q-avantages',
          q: "Quels sont les avantages de travailler chez Nettis ?",
          a: "Nettis Facility offre un environnement de travail respectueux, des horaires flexibles, une formation continue, des équipements de qualité et des possibilités d'évolution. Nous valorisons l'engagement et le professionnalisme de nos équipes."
        }
      ]
    }
  ];

  // Structured data for FAQ
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.flatMap(category => 
      category.questions.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    )
  };

  return (
    <div className="pt-20">
      <SEO
        title="FAQ - Questions fréquentes"
        description="Trouvez les réponses à vos questions sur les services de nettoyage Nettis Facility : tarifs, zones d'intervention, produits écologiques, devis gratuit et plus encore."
        keywords="FAQ nettoyage, questions nettoyage professionnel, devis nettoyage, tarifs nettoyage bureaux, nettoyage écologique Belgique"
        canonicalUrl="https://www.nettis.be/faq"
        structuredData={faqStructuredData}
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-medium text-[#10B981] uppercase tracking-widest mb-4 block">FAQ</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">
              Questions <span className="text-[#1E3A8A]">fréquentes</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Retrouvez les réponses aux questions les plus courantes sur nos services de nettoyage professionnel.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          {faqs.map((category) => (
            <div key={category.id} className="mb-10">
              <h2 className="text-2xl font-bold text-[#1E3A8A] mb-6 flex items-center gap-2">
                <HelpCircle className="w-6 h-6 text-[#10B981]" />
                {category.category}
              </h2>
              <div className="space-y-4">
                {category.questions.map((faq) => {
                  const key = faq.id;
                  const isOpen = openIndex === key;
                  return (
                    <div
                      key={faq.id}
                      className="border border-slate-200 rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenIndex(openIndex === key ? null : key)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <span className="font-semibold text-[#0F172A] pr-4">{faq.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-[#1E3A8A] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[#1E3A8A] flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 py-4 bg-white">
                          <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Vous avez d'autres questions ?</h2>
          <p className="text-blue-100 mb-8">
            Notre équipe est à votre disposition pour répondre à toutes vos questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-3">
                <Mail className="w-5 h-5 mr-2" />
                Nous contacter
              </Button>
            </Link>
            <a href="tel:+3228806650">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3">
                <Phone className="w-5 h-5 mr-2" />
                +32 2 880 66 50
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
